import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { listDependents } from '@brands/services/identity/listDependents';
import { selectAuth } from '@brands/store/selectors/auth';
import { setDependents } from '@brands/store/slices/dependentsSlice';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { useEffect } from 'react';

/**
 * Loads the signed-in guardian's dependents into the store, in the same shape
 * the patient Dashboard does. Sign-out empties that list, and pages that pick a
 * dependent from it (My Care) can be reached straight from an email or a
 * report's Close button without the Dashboard ever mounting, which left their
 * patient dropdown with "Myself" only.
 */
export default function useLoadDependents(): void {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector(selectAuth);
  const userId = userInfo?.id;

  useEffect(() => {
    if (!userId) return undefined;
    let mounted = true;
    (async () => {
      try {
        const dependents = await listDependents(userId, { include_deleted: true });
        if (!mounted) return;
        dispatch(
          setDependents(
            dependents
              .slice()
              .sort((a, b) => b.id - a.id)
              .map((dependent) => ({
                value: dependent.id.toString(),
                label: dependent.full_name,
                status: dependent.status,
                image: getUserAvatar(Number(dependent.id)),
              })),
          ),
        );
      } catch (error: unknown) {
        if (mounted) displayErrorDetails(error);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [dispatch, userId]);
}
